/**
 * FlightLines - Compact departure/arrival lines for the aircraft card.
 *
 * Shows the next departure and/or arrival as color-coded rows between
 * the StatusRowStack and quick action buttons. Each line has a quick-confirm
 * button to mark the event as fulfilled.
 *
 * Movement ledger model: uses upcomingDepartures + pendingReturn.
 */

import { useState } from 'react'
import { PlaneTakeoff, PlaneLanding, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useFlightLineSchedules } from '@/hooks/useFlightLineSchedules'
import { useConfirmScheduledDeparture, useCreateArrivalEvent } from '@/hooks/useAllocationEvents'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { computeFlightLines, type FlightLineDef } from '@/lib/utils/compute-flight-lines'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { AircraftSchedule } from '@/types/aircraft-schedules'

interface FlightLinesProps {
  data: AllocationEnrichedData
}

export function FlightLines({ data }: FlightLinesProps) {
  const { locationId } = useAppContext()
  const { data: scheduleData } = useFlightLineSchedules(data.aircraft.id, locationId)

  const [editSchedule, setEditSchedule] = useState<AircraftSchedule | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  const confirmDeparture = useConfirmScheduledDeparture()
  const confirmArrival = useCreateArrivalEvent()

  if (!scheduleData) return null

  const lines = computeFlightLines(
    scheduleData.upcomingDepartures,
    scheduleData.pendingReturn,
    data.allocation.presence,
  )

  if (lines.length === 0) return null

  const handleConfirm = async (line: FlightLineDef) => {
    if (!locationId) return
    setConfirmingId(line.id)

    try {
      if (line.confirmAction === 'depart') {
        await confirmDeparture.mutateAsync({
          schedule_id: line.schedule.id,
          allocation_id: data.allocationId,
          aircraft_id: data.aircraft.id,
          location_id: locationId,
          destination_icao: line.schedule.airportIcao,
        })
        toast.success(`${data.aircraft.tailNumber} marked as departed`)
      } else if (line.confirmAction === 'arrive') {
        await confirmArrival.mutateAsync({
          allocation_id: data.allocationId,
          aircraft_id: data.aircraft.id,
          location_id: locationId,
        })
        toast.success(`${data.aircraft.tailNumber} marked as returned`)
      }
    } catch {
      toast.error('Failed to confirm event')
    } finally {
      setConfirmingId(null)
    }
  }

  return (
    <>
      <div className="px-3 py-1.5 space-y-0.5 border-t border-border/50">
        {lines.map((line) => (
          <FlightLineRow
            key={line.id}
            line={line}
            isConfirming={confirmingId === line.id}
            onConfirm={() => handleConfirm(line)}
            onEdit={() => setEditSchedule(line.schedule)}
          />
        ))}

      </div>

      {/* Schedule Edit Dialog (edit mode) */}
      {editSchedule && (
        <ScheduleEditDialog
          open={!!editSchedule}
          onOpenChange={(open) => { if (!open) setEditSchedule(null) }}
          schedule={editSchedule}
        />
      )}
    </>
  )
}

// ── Individual flight line row ─────────────────────────

interface FlightLineRowProps {
  line: FlightLineDef
  isConfirming: boolean
  onConfirm: () => void
  onEdit: () => void
}

function FlightLineRow({ line, isConfirming, onConfirm, onEdit }: FlightLineRowProps) {
  const isDeparture = line.type === 'departure'

  const iconBoxClass = isDeparture
    ? 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800'
    : 'bg-green-50 text-green-600 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800'

  const labelClass = isDeparture
    ? 'text-orange-600 dark:text-orange-400'
    : 'text-green-600 dark:text-green-400'

  const confirmBtnClass = isDeparture
    ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-900'
    : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-950 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900'

  const Icon = isDeparture ? PlaneTakeoff : PlaneLanding

  const row = (
    <div
      className={`flex items-center gap-2 py-1 text-[12px] ${line.isMuted ? 'opacity-50' : ''}`}
    >
      {/* Colored icon box */}
      <div
        className={`w-[22px] h-[22px] rounded-[5px] border flex items-center justify-center shrink-0 ${iconBoxClass}`}
      >
        <Icon className="w-[13px] h-[13px]" />
      </div>

      {/* Label + exact time (clickable to edit) */}
      <button
        onClick={onEdit}
        className="flex items-center gap-1 min-w-0 flex-1 text-left hover:opacity-70 transition-opacity"
      >
        <span className={`font-semibold text-[12px] ${labelClass}`}>
          {line.label}
        </span>
        {line.exactTime && (
          <span className="text-muted-foreground text-[11px]">{line.exactTime}</span>
        )}
      </button>

      {/* Route */}
      {line.route && (
        <span className="text-muted-foreground text-[11px] shrink-0">{line.route}</span>
      )}

      {/* Quick-confirm button */}
      {line.confirmLabel && (
        <button
          onClick={(e) => { e.stopPropagation(); onConfirm() }}
          disabled={isConfirming}
          className={`text-[10px] font-semibold px-2 py-0.5 rounded border shrink-0 transition-colors disabled:opacity-50 ${confirmBtnClass}`}
        >
          {isConfirming ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            line.confirmLabel
          )}
        </button>
      )}
    </div>
  )

  if (line.tooltipText) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent side="top" className="text-xs whitespace-pre-line">
            {line.tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return row
}
